import { useSelector } from "react-redux";
import { RootState } from "../../../../store/rootReducer";
import { useTranslation } from "react-i18next";
import { DatePicker, Descriptions, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { ITreeCode, ITreeSubcode } from "../../../../models/treeCodes.model";
import FileUpload from "../../../FileUpload";
import Button from "../../../Button";
import moment from "moment";
import useNotification from "../../../../hooks/useNotification";
import useAxios from "../../../../hooks/useAxios";
import { createIncome, getIncome } from "../../../../api/financials.api";
import { IResponse } from "../../../../models/common.api.model";
import { IncomePageType } from "../../../../constans";
import ErrorResponse from "../../../ErrorResponse";
import { IGetIncome } from "../../../../models/financials.model";

interface IProps {
    getIncomeList: (listType: 'A' | 'P') => void;
    setSelectedRow: (incomeCode: any) => void;
    setPageType: (param: IncomePageType) => void;
    commonCode: string,
    incomeCode?: number;
}

const IncomeCopy = ({getIncomeList, setSelectedRow, setPageType, commonCode, incomeCode} : IProps) => {

    const { t, i18n } = useTranslation();
    const { infoNotification, successNotification } = useNotification();
    const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
    const { treeCodes } = useSelector((state: RootState) => state.treeCodeData);
    // FA0002 = 수입 코드
    const FA0002 = treeCodes.filter(item => (item.commonCode === commonCode && item.treeLevel === 1));
    
    const { commonCodes } = useSelector((state: RootState) => state.commonCode);
    // FB0002 = 입금방법
    const FB0002 = commonCodes.find(item => item.commonCode === 'FB0002')?.detailCode;
    // FB0003 = 입금여부
    const FB0003 = commonCodes.find(item => item.commonCode === 'FB0003')?.detailCode;
    // FB0004 = 통화
    const FB0004 = commonCodes.find(item => item.commonCode === 'FB0004')?.detailCode;
    
    const [account1, setAccount1] = useState<string>();
    const [account2, setAccount2] = useState<string>();
    const [account3, setAccount3] = useState<string>();
    const [account2List, setAccount2List] = useState<ITreeSubcode[]>();
    const [account3List, setAccount3List] = useState<ITreeSubcode[]>();

    const registDate = moment(new Date());  //작성일
    const [incomeDate, setIncomeDate] = useState('');   //입금일
    const [incomeMtd, setIncomeMtd] = useState("ON"); //입금방법
    const [incomeYn, setIncomeYn] = useState("Y");   //입금여부
    const [incomeDesc, setIncomeDesc] = useState('');   //내역
    const [currency, setCurrency] = useState("KRW");    //통화
    const [amount, setAmount] = useState(0);    //입금액
    const [note, setNote] = useState('');   //비고
    // 신규 첨부파일
    const [files, setFiles] = useState<any>([null]);
    const dateArea = useRef<any>();

    const { data: res, isLoading, error, execute } = useAxios<IResponse<Number>>();
    const { data: incomeCopyDetail, isLoading:copyLoading, error:copyError, execute: executeDetail } = useAxios<IGetIncome>();

    useEffect(() => {
        if(incomeCode) {
            executeDetail(getIncome(incomeCode, commonCode));
        }
    }, [incomeCode]);

    useEffect(() => {
        //조회되면 값 셋팅
        if(incomeCopyDetail?.data) {
            setAccount1(incomeCopyDetail.data.firstIncomeAccCode);
            setAccount2(incomeCopyDetail.data.secondIncomeAccCode);
            setAccount3(incomeCopyDetail.data.thirdIncomeAccCode);
            setAccount2List(treeCodes.filter(item => (item.commonCode === commonCode && item.treeLevel === 2 && item.parentDetailCode === Number(incomeCopyDetail.data.firstIncomeAccCode) )))
            setAccount3List(treeCodes.filter(item => (item.commonCode === commonCode && item.treeLevel === 3 && item.parentDetailCode === Number(incomeCopyDetail.data.secondIncomeAccCode) )))
            setIncomeDate(incomeCopyDetail.data.incomeDate);
            setIncomeMtd(incomeCopyDetail.data.incomeMtd);
            setIncomeYn(incomeCopyDetail.data.incomeYn);
            setIncomeDesc(incomeCopyDetail.data.incomeDesc);
            setCurrency(incomeCopyDetail.data.currency);
            setAmount(incomeCopyDetail.data.amount);
            setNote(incomeCopyDetail.data.note);
        }
    }, [incomeCopyDetail]);

    const firstAccountChange = (value : any) => {
        setAccount1(value);
        if(value) {
            setAccount2List(treeCodes.filter(item => (item.commonCode === commonCode && item.treeLevel === 2 && item.parentDetailCode === Number(value) )))
            setAccount3List(undefined);
            setAccount2(undefined);
            setAccount3(undefined);
        }
    }

    const secondAccountChange = (value : any) => {
        setAccount2(value);
        if(value) {
            setAccount3(undefined);
            setAccount3List(treeCodes.filter(item => (item.commonCode === commonCode && item.treeLevel === 3 && item.parentDetailCode === Number(value) )));
        }
    }

    useEffect(() => {
        if(res) {
            if(res.result === "success") {
                successNotification(t('common.message.save1'));
                //리스트페이지 검색조건으로 재검색
                getIncomeList('A');
                //조회페이지로 변경
                setPageType(IncomePageType.VIEW);
                setSelectedRow(res.data);
            }
        }
    }, [res]);

    const onSetFile = (index: number, file: any) => {
        const _files = [...files];
        _files[index] = file;
        setFiles(_files);
    }

    const onAddFile = () => {
        const _files = [...files];
        _files.push(null);
        setFiles(_files);
    }

    const onDeleteFile = (index: number) => {
        const _files = [...files];
        _files.splice(index, 1);
        setFiles(_files);
    }

    const onSave = async () => {
        if(!account3) {
            return infoNotification(t('payment.income.msg2'));
        }

        if(!incomeDate) {            
            return infoNotification(t('payment.income.msg3'));
        }

        if(!amount) {            
            return infoNotification(t('payment.income.msg5'));
        }

        //파라미터 셋팅(form)
        const form = new FormData();
        form.append('campusCode', currentCampus);   //auth.hqYn === 'Y' auth.campus[0]
        form.append('incomeAccCode', account3);
        
        form.append('registDate', registDate.format('YYYYMMDD'));
        form.append('incomeDate', incomeDate);
        form.append('incomeMtd', incomeMtd);
        form.append('incomeYn', incomeYn);
        form.append('incomeDesc', incomeDesc);
        form.append('currency', currency);
        form.append('amount', amount.toString());
        form.append('note', note);
        if (files.length > 0) {
            files.forEach((item: any) => {
                if (item !== null) form.append('files', item);
            });
        }

        execute(createIncome(form));
    }

    return(
        <>
            <ErrorResponse error={error} />
            <div className="comm-bx__between">  
                <h3 className='sub-title'>{t('payment.income.title1')}</h3>
            </div>
            <div className="comm-table__area rowType">
                <table>
                    <colgroup>
                        <col width="15%" />
                        <col width="85%" />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th>{t('system.treecode.account1')}</th>
                            <td>
                                <Select
                                    placeholder={t('payment.income.search2') + ' ' + t('campus.staff.placeholder2')}
                                    className="w-240p"
                                    value={account1}
                                    onChange={(value)=>{
                                        firstAccountChange(value);
                                    }}
                                >
                                    {
                                        FA0002?.map((item : ITreeCode)=>{
                                            return(
                                                <Select.Option
                                                    key={item.detailCode.toString()}
                                                    value={item.detailCode.toString()}
                                                >
                                                    {i18n.language === 'ko' ? item.korName : item.engName}
                                                </Select.Option>
                                            );
                                        })
                                    }
                                </Select>
                            </td>
                        </tr>
                        <tr>
                            <th>{t('system.treecode.account2')}</th>
                            <td>
                                <Select
                                    placeholder={t('payment.income.search2') + ' ' + t('campus.staff.placeholder2')}
                                    className="w-240p"
                                    value={account2}
                                    onChange={(value)=>{
                                        secondAccountChange(value);
                                    }}
                                >
                                    {
                                        account2List?.map((item : ITreeCode)=>{
                                            return(
                                                <Select.Option
                                                    key={item.detailCode.toString()}
                                                    value={item.detailCode.toString()}
                                                >
                                                    {i18n.language === 'ko' ? item.korName : item.engName}
                                                </Select.Option>
                                            );
                                        })
                                    }
                                </Select>
                            </td>
                        </tr>
                        <tr>
                            <th>{t('system.treecode.account3')}</th>
                            <td>
                                <Select
                                    placeholder={t('payment.income.search2') + ' ' + t('campus.staff.placeholder2')}
                                    className="w-240p"
                                    value={account3}
                                    onChange={(value)=>{
                                        setAccount3(value);
                                    }}
                                >
                                    {
                                        account3List?.map((item : ITreeCode)=>{
                                            return(
                                                <Select.Option
                                                    key={item.detailCode.toString()}
                                                    value={item.detailCode.toString()}
                                                >
                                                    {i18n.language === 'ko' ? item.korName : item.engName}
                                                </Select.Option>
                                            );
                                        })
                                    }
                                </Select>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div ref={dateArea} ></div>
            <div className="comm-table__area rowType">
                <table>
                    <colgroup>
                        <col width="15%" />
                        <col width="35%" />
                        <col width="15%" />
                        <col width="35%" />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th>{t('student.student.62')}</th>
                            <td>{registDate.format('YYYY-MM-DD')}</td>
                            <th>{'* ' + t('payment.income.column1')}</th>
                            <td>
                                <div className="date-picker__area">
                                    <DatePicker
                                        value={incomeDate ? moment(incomeDate) : registDate}
                                        onChange={e => {
                                            if (e === null) setIncomeDate('');
                                            else setIncomeDate(moment(e).format('YYYYMMDD'))
                                        }}
                                        format={['YYYY-MM-DD', 'YYYYMMDD']}
                                        getPopupContainer={() => {
                                            return dateArea.current as HTMLElement;
                                        }}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>{'* ' + t('payment.income.search3')}</th>
                            <td>
                                <Select
                                    className="w-100"
                                    value={incomeMtd}
                                    onChange={(value)=>{
                                        setIncomeMtd(value);
                                    }}
                                >
                                    {
                                        FB0002?.map((item)=>{
                                            return(
                                                <Select.Option
                                                    key={item.detailCode}
                                                    value={item.detailCode}
                                                >
                                                    {i18n.language === 'ko' ? item.korName : item.engName}
                                                </Select.Option>
                                            );
                                        })
                                    }
                                </Select>
                            </td>
                            <th>{'* ' + t('payment.income.search4')}</th>
                            <td>
                                <Select
                                    className="w-100"
                                    value={incomeYn}
                                    onChange={(value)=>{
                                        setIncomeYn(value);
                                    }}
                                >
                                    {
                                        FB0003?.map((item)=>{
                                            return(
                                                <Select.Option
                                                    key={item.detailCode}
                                                    value={item.detailCode}
                                                >
                                                    {i18n.language === 'ko' ? item.korName : item.engName}
                                                </Select.Option>
                                            );
                                        })
                                    }
                                </Select>
                            </td>
                        </tr>
                        <tr>
                            <th>{'* ' + t('payment.income.column4')}</th>
                            <td colSpan={3}>
                                <div className="flex" style={{alignItems:'center', gap:'5px'}}>
                                    <Select
                                        className="w-120p text-center"
                                        value={currency}
                                        onChange={(value)=>{
                                            setCurrency(value);
                                        }}
                                    >   
                                        {FB0004?.map((item) => {
                                            return(
                                                <Select.Option
                                                    key={item.detailCode}
                                                    value={item.detailCode}
                                                >
                                                    {i18n.language === 'ko' ? item.korName : item.engName}
                                                </Select.Option>
                                            );
                                        })};
                                    </Select>
                                    <input
                                        type="number"
                                        className="form-control input-text text-right w-210p"
                                        value={amount.toString()}
                                        onChange={e => {
                                            if(Number(e.target.value) < 0) {
                                                return false;
                                            }
                                            setAmount(Number(e.target.value))
                                        }}
                                    />
                                    <b>{t('campus.setting.text2')}</b>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>{t('payment.income.search5')}</th>
                            <td colSpan={3}>
                                <div className="w-100">
                                    <input
                                        type="text"
                                        className="form-control input-text"
                                        value={incomeDesc}
                                        onChange={e => setIncomeDesc(e.target.value)}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>{t('common.column.file')}</th>
                            <td colSpan={3}>
                                {files.map((file: any, index: number) => (
                                    <div className="flex mb-2" key={index}>
                                        <FileUpload
                                        file={files[index]}
                                        setFile={(file: any) => onSetFile(index, file)}
                                        />
                                        <div className="comm-btn__area">
                                            <Button
                                                className="btn-basic small gray ml-2"
                                                onClick={() => onDeleteFile(index)}
                                                text={t('common.button.delete')}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div className="comm-btn__area">
                                    <Button
                                        className="btn-basic pink-line"
                                        onClick={onAddFile}
                                        text={t('common.button.add')}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>{t('common.text.note')}</th>
                            <td colSpan={3}>
                                <textarea
                                    className="w-100"
                                    rows={5}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="comm-bx__between">
                <div></div>
                <div className="comm-btn__area">
                    <Button
                        onClick={onSave}
                        className="btn-basic pink"
                        text={t('common.button.save')}
                        /*writeAuthYn={writeAuthYn}*/
                    />
                </div>
            </div>
        </>
    )
}

export default IncomeCopy;